import React, { useEffect, useMemo, useState } from 'react'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import { useLocation, useNavigate } from 'react-router-dom'
import Dropdown from 'react-bootstrap/Dropdown'
import queryString from 'query-string'
import { DropdownSearchInput } from 'src/components/Reusable/Inputs/DropdownSearchInput'
import { CheckBoxInput } from 'src/components/Reusable/Inputs/CheckBox'
import { useNavigateContext } from '../../../Navigation/NavigateContext'
import {
  FilterCountChip,
  FilterDropdownItem,
  FilterDropdownToggle,
} from '../FilterBar.styles'
import { FilterName, FilterParam } from '../FilterBar.types'
import { useFilterBarValues } from '../FilterBar.context'
import { useFilterLabels } from '../FilterLabelsContext'
import { DropDownMenu } from './FilterDropdown.styles'

export const ReportPlaybookFilterDropdown = React.memo(
  ({ isNested = false }: { isNested?: boolean }): JSX.Element => {
    const location = useLocation()
    const navigate = useNavigate()
    const [searchQuery, setSearchQuery] = useState('')
    const { registerFilter } = useFilterLabels()
    const filterBarValues = useFilterBarValues()
    const {
      navigateState: { parsedQueryParams },
    } = useNavigateContext()

    const searchParamsFallback = useMemo(
      () => queryString.parse(location.search, { arrayFormat: 'bracket' }),
      [location.search],
    )
    const searchParams = parsedQueryParams ?? searchParamsFallback
    const appliedFilters: string[] = useMemo(() => {
      const raw = searchParams[FilterParam.REPORT_PLAYBOOK]
      return Array.isArray(raw) ? raw : raw ? [raw] : []
    }, [searchParams])

    const allOptions = filterBarValues.transformFilter(
      FilterParam.REPORT_PLAYBOOK,
      'reportPlaybook',
    )

    const filteredOptions = useMemo(() => {
      const q = searchQuery.trim().toLowerCase()
      return q
        ? allOptions.filter((opt) =>
            String(opt.label).toLowerCase().includes(q),
          )
        : allOptions
    }, [allOptions, searchQuery])

    // Register labels so active-filter chips show playbook names
    useEffect(() => {
      const valueMap: Record<string, string> = {}
      allOptions.forEach((opt) => {
        valueMap[opt.value] = String(opt.label)
      })
      registerFilter(
        FilterParam.REPORT_PLAYBOOK,
        FilterName.REPORT_PLAYBOOK,
        valueMap,
      )
    }, [allOptions, registerFilter])

    if (!allOptions.length) return <></>

    return (
      <Dropdown autoClose="outside">
        <Dropdown.Toggle
          as={FilterDropdownToggle}
          id="report-playbook-filter-dropdown"
          $hasSelection={appliedFilters.length > 0}
          $isNested={isNested}
        >
          <p>{FilterName.REPORT_PLAYBOOK}</p>
          {appliedFilters.length > 0 && (
            <FilterCountChip ml="xxs">{appliedFilters.length}</FilterCountChip>
          )}
        </Dropdown.Toggle>
        <DropDownMenu>
          <DropdownSearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            autoFocus
          />
          {filteredOptions.map((opt) => {
            const isChecked = appliedFilters.includes(opt.value)
            const newFilters = isChecked
              ? appliedFilters.filter((f) => f !== opt.value)
              : [...appliedFilters, opt.value]
            const href = queryString.stringifyUrl(
              {
                url: location.pathname,
                query: {
                  ...searchParams,
                  [FilterParam.REPORT_PLAYBOOK]: newFilters,
                },
              },
              { arrayFormat: 'bracket' },
            )
            return (
              <FilterDropdownItem key={opt.value} replace to={href}>
                <CheckBoxInput
                  id={opt.value}
                  label={String(opt.label)}
                  checked={isChecked}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => navigate(href, { replace: true })}
                  tabIndex={-1}
                  requiredCapabilities={[CAPABILITY.CAN_VIEW_REPORTS]}
                />
              </FilterDropdownItem>
            )
          })}
        </DropDownMenu>
      </Dropdown>
    )
  },
)
